import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { SettingsIcon, CalendarIcon, DollarSignIcon, MapIcon, ShieldAlertIcon, FileTextIcon, XIcon } from 'lucide-react';
export function SystemConfiguration() {
  const [activeTab, setActiveTab] = useState<'ban' | 'tariff' | 'geo' | 'fine'>('ban');
  // Propose Rate Change Modal
  const [showRateModal, setShowRateModal] = useState(false);
  const [rateForm, setRateForm] = useState({
    vesselType: '',
    registeredFee: '',
    unregisteredFee: ''
  });
  // Fine Policy Data
  const [entryFees, setEntryFees] = useState([{
    id: 1,
    category: 'Entry / Passage Fee',
    vesselType: 'Launch (over 100 feet)',
    registeredFee: '1,500.00',
    unregisteredFee: '6,000.00'
  }, {
    id: 2,
    category: 'Entry / Passage Fee',
    vesselType: 'Launch (50 to 100 feet)',
    registeredFee: '1,200.00',
    unregisteredFee: '8,500.00'
  }, {
    id: 3,
    category: 'Entry / Passage Fee',
    vesselType: 'Launch (under 50 feet)',
    registeredFee: '950.00',
    unregisteredFee: '5,000.00'
  }, {
    id: 4,
    category: 'Entry / Passage Fee',
    vesselType: 'Trawler / Sampan',
    registeredFee: '500.00',
    unregisteredFee: '1,500.00'
  }, {
    id: 5,
    category: 'Entry / Passage Fee',
    vesselType: 'Country Boat (Deshi Nouka)',
    registeredFee: '150.00',
    unregisteredFee: '500.00'
  }, {
    id: 6,
    category: 'Entry / Passage Fee',
    vesselType: 'Speedboat',
    registeredFee: '3,000.00',
    unregisteredFee: '6,000.00'
  }, {
    id: 7,
    category: 'Entry / Passage Fee',
    vesselType: 'Speedboat (with Motor Vessel)',
    registeredFee: '1,000.00',
    unregisteredFee: '2,000.00'
  }, {
    id: 8,
    category: 'Entry / Passage Fee',
    vesselType: 'Tourist Boat (Jali Boat)',
    registeredFee: '350.00',
    unregisteredFee: '1,500.00'
  }, {
    id: 9,
    category: 'Entry / Passage Fee',
    vesselType: 'Helicopter / Seaplane (per trip)',
    registeredFee: '2,500.00',
    unregisteredFee: '6,000.00'
  }, {
    id: 10,
    category: 'Stay Fee (per day)',
    vesselType: 'Per Deer / Animal',
    registeredFee: '600.00',
    unregisteredFee: '3,000.00'
  }, {
    id: 11,
    category: 'Stay Fee (per day)',
    vesselType: 'Trawler, Speedboat, Country Boat (with Motor Vessel)',
    registeredFee: '300.00',
    unregisteredFee: '900.00'
  }]);
  const fineColumns = [{
    key: 'category',
    label: 'Fee Category'
  }, {
    key: 'vesselType',
    label: 'Vessel / Vehicle Type'
  }, {
    key: 'registeredFee',
    label: 'Registered (৳)',
    render: (val: string) => <span className="font-mono font-medium text-green-700" data-id="element-2483">৳ {val}</span>
  }, {
    key: 'unregisteredFee',
    label: 'Unregistered (৳)',
    render: (val: string) => <span className="font-mono font-medium text-red-700" data-id="element-2484">৳ {val}</span>
  }];
  // Mock Data
  const banCalendar = [{
    resource: 'Honey',
    startDate: '2024-06-01',
    endDate: '2024-07-31',
    status: 'Active',
    approvedBy: 'Admin'
  }, {
    resource: 'Fish',
    startDate: '2024-05-15',
    endDate: '2024-08-15',
    status: 'Pending Approval',
    approvedBy: '-'
  }, {
    resource: 'Wood',
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    status: 'Active',
    approvedBy: 'Admin'
  }];
  const tariffTable = [{
    resource: 'Honey',
    unit: 'Kg',
    baseFee: '50',
    tax: '15%',
    status: 'Active'
  }, {
    resource: 'Fish',
    unit: 'Kg',
    baseFee: '20',
    tax: '10%',
    status: 'Active'
  }, {
    resource: 'Golpata',
    unit: 'Maund',
    baseFee: '100',
    tax: '15%',
    status: 'Pending Approval'
  }];
  const geoHierarchy = [{
    level: 'Division',
    name: 'Khulna',
    parent: '-'
  }, {
    level: 'Range',
    name: 'Chandpai',
    parent: 'Khulna'
  }, {
    level: 'Station',
    name: 'Karamjal',
    parent: 'Chandpai'
  }, {
    level: 'Compartment',
    name: 'C-12',
    parent: 'Karamjal'
  }];
  const banColumns = [{
    key: 'resource',
    label: 'Resource'
  }, {
    key: 'startDate',
    label: 'Start Date'
  }, {
    key: 'endDate',
    label: 'End Date'
  }, {
    key: 'status',
    label: 'Status',
    render: (val: string) => <span className={`px-2 py-1 rounded-full text-xs font-medium ${val === 'Active' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`} data-id="element-2485">
          {val}
        </span>
  }, {
    key: 'approvedBy',
    label: 'Approved By'
  }];
  const tariffColumns = [{
    key: 'resource',
    label: 'Resource'
  }, {
    key: 'unit',
    label: 'Unit'
  }, {
    key: 'baseFee',
    label: 'Base Fee (৳)'
  }, {
    key: 'tax',
    label: 'Tax (%)'
  }, {
    key: 'status',
    label: 'Status',
    render: (val: string) => <span className={`px-2 py-1 rounded-full text-xs font-medium ${val === 'Active' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`} data-id="element-2486">
          {val}
        </span>
  }];
  const geoColumns = [{
    key: 'level',
    label: 'Level'
  }, {
    key: 'name',
    label: 'Name'
  }, {
    key: 'parent',
    label: 'Parent Node'
  }];
  const handleProposeRate = () => {
    if (!rateForm.vesselType || !rateForm.registeredFee || !rateForm.unregisteredFee) return;
    const newEntry = {
      id: entryFees.length + 1,
      category: 'Entry / Passage Fee',
      vesselType: rateForm.vesselType,
      registeredFee: parseFloat(rateForm.registeredFee).toLocaleString('en-US', {
        minimumFractionDigits: 2
      }),
      unregisteredFee: parseFloat(rateForm.unregisteredFee).toLocaleString('en-US', {
        minimumFractionDigits: 2
      })
    };
    setEntryFees([...entryFees, newEntry]);
    setRateForm({
      vesselType: '',
      registeredFee: '',
      unregisteredFee: ''
    });
    setShowRateModal(false);
  };
  return <div className="space-y-6" data-id="element-2487">
      <div data-id="element-2488">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-id="element-2489">
          <SettingsIcon className="text-[#1B5E20]" data-id="element-2490" />
          System Configuration
        </h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-2491">
          Global settings, ban periods, tariffs, fine policy, and geographical
          hierarchy
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-2492">
        <ShieldAlertIcon className="text-blue-600 mt-0.5" size={20} data-id="element-2493" />
        <div data-id="element-2494">
          <h4 className="font-semibold text-blue-900" data-id="element-2495">
            Maker-Checker Policy Active
          </h4>
          <p className="text-sm text-blue-700 mt-1" data-id="element-2496">
            Any changes to Ban Dates or Tariffs require Admin/Financial Admin
            approval before taking effect across stations.
          </p>
        </div>
      </div>

      <div className="border-b border-gray-200" data-id="element-2497">
        <div className="flex gap-4" data-id="element-2498">
          <button onClick={() => setActiveTab('ban')} className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'ban' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2499">
            <CalendarIcon size={16} data-id="element-2500" />
            Ban Calendar
          </button>
          <button onClick={() => setActiveTab('tariff')} className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'tariff' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2501">
            <DollarSignIcon size={16} data-id="element-2502" />
            Tariff Master
          </button>
          <button onClick={() => setActiveTab('fine')} className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'fine' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2503">
            <FileTextIcon size={16} data-id="element-2504" />
            Fine Policy
          </button>
          <button onClick={() => setActiveTab('geo')} className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'geo' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2505">
            <MapIcon size={16} data-id="element-2506" />
            Geo Hierarchy
          </button>
        </div>
      </div>

      {activeTab === 'ban' && <div className="space-y-4" data-id="element-2507">
          <div className="flex justify-end" data-id="element-2508">
            <button className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] text-sm font-medium" data-id="element-2509">
              + Propose Ban Update
            </button>
          </div>
          <DataTable columns={banColumns} data={banCalendar} searchPlaceholder="Search resources..." data-id="element-2510" />
        </div>}

      {activeTab === 'tariff' && <div className="space-y-4" data-id="element-2511">
          <div className="flex justify-end" data-id="element-2512">
            <button className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] text-sm font-medium" data-id="element-2513">
              + Propose Tariff Change
            </button>
          </div>
          <DataTable columns={tariffColumns} data={tariffTable} searchPlaceholder="Search resources..." data-id="element-2514" />
        </div>}

      {activeTab === 'geo' && <div className="space-y-4" data-id="element-2515">
          <div className="flex justify-end" data-id="element-2516">
            <button className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] text-sm font-medium" data-id="element-2517">
              + Add Node
            </button>
          </div>
          <DataTable columns={geoColumns} data={geoHierarchy} searchPlaceholder="Search locations..." data-id="element-2518" />
        </div>}

      {activeTab === 'fine' && <div className="space-y-4" data-id="element-2519">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-id="element-2520">
            <ShieldAlertIcon className="text-amber-600 mt-0.5" size={20} data-id="element-2521" />
            <div data-id="element-2522">
              <h4 className="font-semibold text-amber-900" data-id="element-2523">
                Fine & Fee Policy — Vessel Based Rates
              </h4>
              <p className="text-sm text-amber-700 mt-1" data-id="element-2524">
                Fee amounts are determined by vessel type and registration
                status. Registered vessels receive reduced rates. All amounts
                are in BDT (৳).
              </p>
            </div>
          </div>

          <div className="flex justify-end" data-id="element-2525">
            <button onClick={() => setShowRateModal(true)} className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] text-sm font-medium" data-id="element-2526">
              + Propose Rate Change
            </button>
          </div>

          <DataTable columns={fineColumns} data={entryFees} onDataChange={setEntryFees} searchPlaceholder="Search vessel types..." data-id="element-2527" />
        </div>}

      {/* Propose Rate Change Modal */}
      {showRateModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-2528">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6" data-id="element-2529">
            <div className="flex items-center justify-between mb-6" data-id="element-2530">
              <h3 className="text-lg font-bold text-gray-900" data-id="element-2531">
                Propose New Rate
              </h3>
              <button onClick={() => setShowRateModal(false)} className="p-1 hover:bg-gray-100 rounded-lg" data-id="element-2532">
                <XIcon size={20} className="text-gray-500" data-id="element-2533" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-2534">
              <div data-id="element-2535">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2536">
                  Vessel Type *
                </label>
                <select value={rateForm.vesselType} onChange={e => setRateForm({
              ...rateForm,
              vesselType: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2537">
                  <option value="" data-id="element-2538">Select Vessel Type</option>
                  <option value="Boat" data-id="element-2539">Boat</option>
                  <option value="Launch" data-id="element-2540">Launch</option>
                  <option value="Trawler" data-id="element-2541">Trawler</option>
                  <option value="Rescue Boat" data-id="element-2542">Rescue Boat</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-2543">
                <div data-id="element-2544">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2545">
                    Registered Fee (৳) *
                  </label>
                  <input type="number" value={rateForm.registeredFee} onChange={e => setRateForm({
                ...rateForm,
                registeredFee: e.target.value
              })} placeholder="0.00" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2546" />
                </div>
                <div data-id="element-2547">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2548">
                    Unregistered Fee (৳) *
                  </label>
                  <input type="number" value={rateForm.unregisteredFee} onChange={e => setRateForm({
                ...rateForm,
                unregisteredFee: e.target.value
              })} placeholder="0.00" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2549" />
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-2550">
                <p className="text-xs text-blue-700" data-id="element-2551">
                  <strong data-id="element-2552">Note:</strong> Proposed rate changes require
                  Admin/Financial Admin approval before taking effect.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6" data-id="element-2553">
              <button onClick={() => setShowRateModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium" data-id="element-2554">
                Cancel
              </button>
              <button onClick={handleProposeRate} disabled={!rateForm.vesselType || !rateForm.registeredFee || !rateForm.unregisteredFee} className="px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2555">
                Submit Proposal
              </button>
            </div>
          </div>
        </div>}
    </div>;
}